import { Skeleton } from 'antd';
import {
  AvatarComponent,
  PrimaryButton,
  MenuDropdown,
  BreadCrumb,
  dynamicNoSsrWithSkeleton,
  getColor,
  getSize,
  styleVariables,
} from '@lib/common';
import type { MenuProps } from 'antd';
import { useTranslation } from 'next-i18next';
import {
  LogoutOutlined,
  UserOutlined,
  DownOutlined,
  CheckOutlined,
  CrownOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthentication, useHelpMenu } from '../../hooks';
import {
  currentLocaleSelector,
  getTestMakerLocaleStatusSelector,
  PageLayoutStatus,
  signOutStatusSelector,
} from '../../redux/layout';
import { useRouter } from 'next/router';
import { getUseCase } from '@lib/plugin-redux-core';
import {
  CHANGE_TEST_MAKER_LOCALE_USE_CASE,
  GET_TEST_MAKER_LOCALE_USE_CASE,
  SIGN_OUT_USE_CASE,
} from '@module/user';
import { PathBuilder, routeManager } from '@lib/route-manager';
import { buildManageSubscriptionUrl } from '../../utils/auth-guard.util';
import styled from 'styled-components';
import Image from 'next/image';
import { useSpaceFolderBreadcrumbExtraItems } from '../space/hooks';
import { buildClassBreadcrumb } from '../classes/class-breadcrumb.utils';
import { CLASS_SECTION_ROUTE, isClassSectionLink } from '@module/class-components';
import type { RootReducerState } from '../../redux/root.reducer';

const LogoutConfirmModalDynamic = dynamic(
  () => import('./log-out-confirm.modal'),
  dynamicNoSsrWithSkeleton,
);

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en', flag: '/icons/us-flag.svg' },
  { label: 'Tiếng Việt', value: 'vi', flag: '/icons/vn-flag.svg' },
] as const;

const capitalizeFirstLetter = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const StyledLanguageSelection = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${getSize('gap8')};
  width: 160px;
  min-width: 160px;
  height: ${getSize('padding40')};
  padding: 0 ${getSize('padding12')};
  border: 1px solid ${getColor('shapeBorder')};
  border-radius: ${getSize('radiusSm')};
  background: ${getColor('fillBgWhite')};
  color: ${getColor('contentTitle')};
  font-family: inherit;
  font-size: ${getSize('typo14')};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible,
  &[aria-expanded='true'] {
    border-color: ${getColor('fillIntActive')};
  }

  &:focus-visible,
  &[aria-expanded='true'] {
    outline: none;
    box-shadow: 0 0 0 2px ${getColor('fillIconBgDefault')};
  }

  .language-selection-arrow {
    flex-shrink: 0;
    color: ${getColor('contentBody')};
    font-size: ${getSize('typo12')};
    pointer-events: none;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  gap: ${getSize('gap12')};

  > :first-child {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 768px) {
    gap: ${getSize('gap8')};

    > :first-child nav {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .language-selection,
    .language-skeleton {
      width: 96px;
      min-width: 96px;
    }

    .language-selection .language-full {
      display: none;
    }

    .language-selection .language-short {
      display: inline;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${getSize('gap16')};

  @media (max-width: 768px) {
    min-width: 0;
    max-width: calc(100% - ${getSize('gap8')});
    gap: ${getSize('gap8')};
  }
`;

const HelpTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 8px;
  padding: 0;
  border: none;
  background: transparent;
  white-space: nowrap;
  color: ${styleVariables.colors.colorPrimaryBase};
  font: inherit;
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  .help-trigger-arrow {
    font-size: 10px;
  }

  &:hover,
  &:focus-visible {
    opacity: 0.85;
  }

  @media (max-width: 360px) {
    min-width: 0;
    max-width: 96px;
    margin-right: 0;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .anticon {
      flex-shrink: 0;
    }
  }
`;

const LanguageOptionLabel = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: ${getSize('gap8')};
  color: inherit;

  .language-selected-icon {
    flex-shrink: 0;
    margin-left: auto;
  }

  .flag-icon {
    flex-shrink: 0;
    border-radius: 2px;
  }

  .language-short {
    display: none;
  }
`;

const LanguageSkeletonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSize('gap8')};
  width: 160px;
  min-width: 160px;
  height: ${getSize('padding40')};
  padding: 0 ${getSize('padding12')};
  border: 1px solid ${getColor('shapeBorder')};
  border-radius: ${getSize('radiusSm')};
  background: ${getColor('fillBgWhite')};

  .language-skeleton-text {
    flex: 1;
    min-width: 0;
  }

  .ant-skeleton-button {
    max-width: 100%;
  }
`;

const LanguageSelectionSkeleton = memo(() => (
  <LanguageSkeletonWrapper className="language-skeleton">
    <Skeleton.Avatar
      active
      shape="square"
      size={16}
      style={{ width: 16, height: 12, borderRadius: 2, minWidth: 'unset' }}
    />
    <Skeleton.Button
      className="language-skeleton-text"
      active
      size="small"
      style={{ width: 64, minWidth: 'unset', height: 16, borderRadius: 4 }}
    />
    <Skeleton.Avatar
      active
      shape="square"
      size={10}
      style={{ width: 10, height: 10, borderRadius: 2, minWidth: 'unset' }}
    />
  </LanguageSkeletonWrapper>
));

LanguageSelectionSkeleton.displayName = 'LanguageSelectionSkeleton';

const renderLanguageLabel = (option: (typeof LANGUAGE_OPTIONS)[number], selected = false) => (
  <LanguageOptionLabel>
    <Image src={option.flag} alt={option.label} width={16} height={12} className="flag-icon" />
    <span className="language-full">{option.label}</span>
    <span className="language-short">{option.value.toUpperCase()}</span>
    {selected && <CheckOutlined aria-hidden className="language-selected-icon" />}
  </LanguageOptionLabel>
);

const HeaderContainer = memo(() => {
  const { t } = useTranslation('common');
  const { t: classT } = useTranslation('class');
  const classDetail = useSelector((state: RootReducerState) => state.classes.detail);
  const { publicRuntimeConfig } = getConfig();
  const { profile, isTokenValid } = useAuthentication();
  const [isOpenLogoutConfirmModal, setModalAlertVisibility] = useState(false);
  const [isLanguageMenuOpen, setLanguageMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const signOutStatus = useSelector(signOutStatusSelector);
  const router = useRouter();
  const currentLocale = useSelector(currentLocaleSelector);
  const getTestMakerLocaleStatus = useSelector(getTestMakerLocaleStatusSelector);
  const { helpLabel, helpLinks } = useHelpMenu();
  const helpMenu = useMemo<MenuProps>(
    () => ({
      items: helpLinks.map((link) => ({
        key: link.key,
        label: link.label,
        icon: link.icon,
        onClick: link.onClick,
      })),
    }),
    [helpLinks],
  );

  const getTestMakerLocaleUseCase = useMemo(
    () => getUseCase(GET_TEST_MAKER_LOCALE_USE_CASE, dispatch),
    [dispatch],
  );
  const signOutForAppUseCase = useMemo(() => getUseCase(SIGN_OUT_USE_CASE, dispatch), [dispatch]);
  const changeTestMakerLocaleUseCase = useMemo(
    () => getUseCase(CHANGE_TEST_MAKER_LOCALE_USE_CASE, dispatch),
    [dispatch],
  );

  useEffect(() => {
    getTestMakerLocaleUseCase.execute(null);
  }, [getTestMakerLocaleUseCase]);

  useEffect(() => {
    if (currentLocale && router.locale !== currentLocale) {
      router.push(router.asPath, router.asPath, { locale: currentLocale });
    }
  }, [currentLocale, router, router.locale, router.asPath]);

  useEffect(() => {
    if (signOutStatus === PageLayoutStatus.SUCCESS) {
      router.push(
        new PathBuilder()
          .withBasePath(routeManager.standard.identity.fullPath.signOut.path)
          .withQueryParam('clientId', publicRuntimeConfig.clientId)
          .withQueryParam('redirectUrl', router.asPath)
          .build(),
      );
    }
  }, [signOutStatus, router, router.asPath, publicRuntimeConfig.clientId]);

  const fullName = useMemo(
    () =>
      `${capitalizeFirstLetter(profile?.firstName)} ${capitalizeFirstLetter(profile?.lastName)}`.trim(),
    [profile?.firstName, profile?.lastName],
  );

  const handleSignOutSubmit = useCallback(() => {
    signOutForAppUseCase.execute({
      clientId: publicRuntimeConfig.clientId,
    });
  }, [publicRuntimeConfig.clientId, signOutForAppUseCase]);

  const handleNavigateToUserProfilePage = useCallback(() => {
    const url = new PathBuilder()
      .withBasePath(routeManager.standard.identity.fullPath.accountSetting.updateProfile.path)
      .withQueryParam('clientId', publicRuntimeConfig.clientId)
      .build();

    window.open(url, '_blank');
  }, [publicRuntimeConfig.clientId]);

  const handleNavigateToManageSubscription = useCallback(() => {
    window.open(buildManageSubscriptionUrl(), '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenLogoutModal = useCallback(() => {
    setModalAlertVisibility(true);
  }, []);

  const handleCloseLogoutModal = useCallback(() => {
    setModalAlertVisibility(false);
  }, []);

  const items: MenuProps['items'] = useMemo(
    () => [
      {
        key: '1',
        label: t('userProfile'),
        icon: <UserOutlined />,
        onClick: handleNavigateToUserProfilePage,
      },
      {
        key: '2',
        label: t('manageSubscription'),
        icon: <CrownOutlined />,
        onClick: handleNavigateToManageSubscription,
      },
      {
        key: '3',
        label: t('signOut'),
        icon: <LogoutOutlined />,
        onClick: handleOpenLogoutModal,
      },
    ],
    [t, handleNavigateToUserProfilePage, handleNavigateToManageSubscription, handleOpenLogoutModal],
  );

  const handleLogin = useCallback(() => {
    router.push(routeManager.standard.identity.fullPath.authentication.path);
  }, [router]);

  const handleLocaleChange = useCallback(
    (locale: string) => {
      changeTestMakerLocaleUseCase.execute(locale);
      router.push(router.asPath, router.asPath, { locale });
    },
    [changeTestMakerLocaleUseCase, router],
  );

  const languageSelection = useMemo(() => {
    if (getTestMakerLocaleStatus === PageLayoutStatus.EXECUTE) {
      return <LanguageSelectionSkeleton />;
    }

    const locale = router.locale || currentLocale || 'en';
    const selectedLanguage =
      LANGUAGE_OPTIONS.find((option) => option.value === locale) || LANGUAGE_OPTIONS[0];

    return (
      <MenuDropdown
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ minWidth: 192 }}
        open={isLanguageMenuOpen}
        onOpenChange={setLanguageMenuOpen}
        menu={{
          selectable: true,
          selectedKeys: [selectedLanguage.value],
          items: LANGUAGE_OPTIONS.map((option) => ({
            key: option.value,
            label: renderLanguageLabel(option, option.value === selectedLanguage.value),
          })),
          onClick: ({ key }) => {
            setLanguageMenuOpen(false);
            if (key !== selectedLanguage.value) handleLocaleChange(key);
          },
        }}
      >
        <StyledLanguageSelection
          type="button"
          className="language-selection"
          aria-haspopup="menu"
          aria-expanded={isLanguageMenuOpen}
        >
          {renderLanguageLabel(selectedLanguage)}
          <DownOutlined aria-hidden className="language-selection-arrow" />
        </StyledLanguageSelection>
      </MenuDropdown>
    );
  }, [
    currentLocale,
    getTestMakerLocaleStatus,
    handleLocaleChange,
    isLanguageMenuOpen,
    router.locale,
  ]);

  const spaceFolderExtraItems = useSpaceFolderBreadcrumbExtraItems();
  const classId = typeof router.query.id === 'string' ? router.query.id : undefined;
  const classBreadcrumb = buildClassBreadcrumb({
    pathname: router.pathname,
    section: router.query.section,
    menu: typeof router.query.menu === 'string' ? router.query.menu : undefined,
    classId,
    className:
      classDetail && classDetail.id === classId
        ? classDetail?.name
        : typeof router.query.name === 'string'
          ? router.query.name
          : undefined,
    localePrefix:
      router.locale && router.locale !== router.defaultLocale ? `/${router.locale}` : '',
    labels: {
      home: classT('breadcrumb.home'),
      classes: classT('breadcrumb.classes'),
      classFallback: classT('breadcrumb.classFallback'),
      students: classT('breadcrumb.students'),
      sections: {
        dashboard: classT('navigation.dashboard'),
        members: classT('navigation.members'),
        assignments: classT('navigation.assignments'),
        announcements: classT('navigation.announcements'),
        information: classT('navigation.information'),
      },
    },
  });

  return (
    <>
      <HeaderWrapper>
        <BreadCrumb
          root="bricks"
          rootPageName="Your work"
          url={router.asPath}
          baseUrl={publicRuntimeConfig.env.APP_BASE_URL}
          menuOverrides={{ examination: 'examination', practice: 'practice' }}
          level={1}
          extraItems={spaceFolderExtraItems}
          items={classBreadcrumb}
          onNavigate={
            classBreadcrumb
              ? (href) => {
                  router.push(href, undefined, {
                    shallow:
                      router.pathname === CLASS_SECTION_ROUTE && isClassSectionLink(href, classId),
                    scroll: false,
                  });
                }
              : undefined
          }
          t={t}
        />
        <HeaderActions>
          <MenuDropdown
            trigger={['click']}
            placement="bottomRight"
            menu={helpMenu}
            overlayStyle={{ minWidth: 192 }}
          >
            <HelpTrigger type="button" aria-haspopup="menu">
              <QuestionCircleOutlined aria-hidden="true" />
              {helpLabel}
              <DownOutlined aria-hidden="true" className="help-trigger-arrow" />
            </HelpTrigger>
          </MenuDropdown>
          {languageSelection}

          {isTokenValid ? (
            <AvatarComponent userName={fullName} email={profile?.email} settingMenu={items} />
          ) : (
            <PrimaryButton onClick={handleLogin}>{t('login')}</PrimaryButton>
          )}
        </HeaderActions>
      </HeaderWrapper>

      <LogoutConfirmModalDynamic
        open={isOpenLogoutConfirmModal}
        onClose={handleCloseLogoutModal}
        onConfirm={handleSignOutSubmit}
        isSigningOut={signOutStatus === PageLayoutStatus.EXECUTE}
      />
    </>
  );
});

HeaderContainer.displayName = 'HeaderContainer';

export default HeaderContainer;
